import React, { useState, useEffect } from "react";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineExclamationTriangle,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
} from "react-icons/hi2";
import { authService } from "../../services";
import masterDataService from "../../services/masterDataService";
import SharedModal from "../../components/common/SharedModal";
import PageLoading from "../../components/common/PageLoading";

const LossReasons = () => {
  const [lossReasons, setLossReasons] = useState([]);
  const [lossTypes, setLossTypes] = useState([]);
  const [lossSubTypes, setLossSubTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingLossReason, setEditingLossReason] = useState(null);
  const [formData, setFormData] = useState({
    reason: "",
    lossTypeId: "",
    lossSubTypeId: "",
    status: "Active",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadData();
    } else {
      setError("Please login to view loss reasons");
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (formData.lossTypeId && tenantId) {
      loadSubTypes(formData.lossTypeId);
    } else {
      setLossSubTypes([]);
    }
  }, [formData.lossTypeId, tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const [reasonsData, typesData] = await Promise.all([
        masterDataService.getLossReasons(tenantId),
        masterDataService.getLossTypes(tenantId),
      ]);
      setLossReasons(reasonsData);
      setLossTypes(typesData);
    } catch (err) {
      setError("Failed to load data. Please try again.");
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadSubTypes = async (typeId) => {
    try {
      const subTypes = await masterDataService.getLossSubTypes(
        tenantId,
        typeId,
      );
      setLossSubTypes(subTypes);
    } catch (err) {
      console.error("Error loading sub types", err);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredLossReasons = lossReasons.filter(
    (item) =>
      item.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.lossType?.lossTypeName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      item.lossSubType?.subTypeName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()),
  );

  const handleAdd = () => {
    setEditingLossReason(null);
    setFormData({
      reason: "",
      lossTypeId: "",
      lossSubTypeId: "",
      status: "Active",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingLossReason(item);
    setFormData({
      reason: item.reason || "",
      lossTypeId: item.lossTypeId || "",
      lossSubTypeId: item.lossSubTypeId || "",
      status: item.status || "Active",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError("");
      await masterDataService.deleteLossReason(id);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete loss reason. Please try again.");
      console.error("Error deleting loss reason:", err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.reason.trim()) {
      errors.reason = "Reason is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const payload = {
        tenantId: parseInt(tenantId),
        reason: formData.reason.trim(),
        lossTypeId: formData.lossTypeId ? parseInt(formData.lossTypeId) : null,
        lossSubTypeId: formData.lossSubTypeId
          ? parseInt(formData.lossSubTypeId)
          : null,
        status: formData.status,
      };

      if (editingLossReason) {
        payload.lossReasonId = editingLossReason.lossReasonId;
        await masterDataService.updateLossReason(
          editingLossReason.lossReasonId,
          payload,
        );
      } else {
        await masterDataService.createLossReason(payload);
      }

      await loadData();
      setShowModal(false);
      setEditingLossReason(null);
    } catch (err) {
      console.error("Error saving loss reason:", err);
      let errMsg = "Failed to save loss reason.";
      if (err.response?.data?.message) errMsg = err.response.data.message;
      else if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        errMsg = Object.values(errors).flat().join(", ");
      } else if (typeof err.response?.data === "string") {
        errMsg = err.response.data;
      }
      setError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLossReason(null);
    setFormData({
      reason: "",
      lossTypeId: "",
      lossSubTypeId: "",
      status: "Active",
    });
    setFormErrors({});
  };

  if (loading) return <PageLoading message="Loading loss reasons..." />;

  return (
    <div className="loss-types-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Loss Reasons</h1>
          <p>Manage loss reasons for standardized reporting</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleAdd}>
            <HiOutlinePlus />
            Add Loss Reason
          </button>
        </div>
      </div>

      {/* <div className="page-header">
        <div className="header-content">
          <h2>Loss Reasons</h2>
          <p>Manage loss reasons for standardized reporting</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus/>
          Add Loss Reason
        </button>
      </div> */}

      {error && <div className="error-message">{error}</div>}

      <div className="table-container pad10">
        <div className="table-header kizantable ">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
        </div>

        <table className="data-table">
          <thead>
            <tr>
              <th>Reason</th>
              <th>Loss Type</th>
              <th>Loss Sub Type</th>
              <th>Status</th>
              <th style={{ width: "100px", textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLossReasons.map((item) => (
              <tr key={item.lossReasonId}>
                <td>{item.reason}</td>
                <td>{item.lossType?.lossTypeName || "-"}</td>
                <td>{item.lossSubType?.subTypeName || "-"}</td>
                <td>
                  <span
                    className={`status-badge ${item.status?.toLowerCase()}`}
                  >
                    {item.status || "Active"}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon-edit"
                      onClick={() => handleEdit(item)}
                    >
                      <HiOutlinePencil />
                    </button>
                    <button
                      className="btn-icon-delete"
                      onClick={() => setDeleteConfirm(item)}
                    >
                      <HiOutlineTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingLossReason ? "Edit Loss Reason" : "New Loss Reason"}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="loss-reason-form"
              className="btn-primary btn"
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="loss-reason-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label>Reason *</label>
              <input
                type="text"
                value={formData.reason}
                onChange={(e) =>
                  setFormData({ ...formData, reason: e.target.value })
                }
                className={formErrors.reason ? "error" : ""}
              />
              {formErrors.reason && (
                <span className="field-error">{formErrors.reason}</span>
              )}
            </div>

            <div className="form-group">
              <label>Loss Type</label>
              <select
                value={formData.lossTypeId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    lossTypeId: e.target.value,
                    lossSubTypeId: "",
                  })
                }
              >
                <option value="">Select Loss Type</option>
                {lossTypes.map((lt) => (
                  <option key={lt.lossTypeId} value={lt.lossTypeId}>
                    {lt.lossTypeName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Loss Sub Type</label>
              <select
                value={formData.lossSubTypeId}
                onChange={(e) =>
                  setFormData({ ...formData, lossSubTypeId: e.target.value })
                }
                disabled={!formData.lossTypeId}
              >
                <option value="">Select Sub Type</option>
                {lossSubTypes.map((lst) => (
                  <option key={lst.subTypeId} value={lst.subTypeId}>
                    {lst.subTypeName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </form>
        </div>
      </SharedModal>

      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              className="btn-secondary btn "
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              className="btn-danger btn"
              onClick={() => handleDelete(deleteConfirm.lossReasonId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.reason}</strong>?
          </p>
          <p className="warning-text">
            This will mark the reason as Inactive.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default LossReasons;
